<template>
  <!-- guard position: relative -->
  <div>
    <div
      :class="rootClassnames"
      @mouseover="onMouseover"
      @mouseout="onMouseout"
    >
      <IconCheckCircle
        :class="[$style.alert__icon, $style['alert__icon--success']]"
        v-if="isPositive"
      />
      <IconExclamationCircle
        v-else
        :class="[$style.alert__icon, $style[`alert__icon--${type}`]]"
      />
      <p :class="[$style.alert__message, $style[`alert__message--${type}`]]">
        {{ message }}
      </p>
      <ButtonClose
        :class="[$style.alert__button, $style[`alert__button--${type}`]]"
        @click="onClose"
      />
    </div>
  </div>
</template>

<script lang="ts">
import type { PropType } from 'vue'
import { defineComponent } from 'vue'
import ButtonClose from '@/components/atoms/ButtonClose.vue'
import IconCheckCircle from '@/components/icons/IconCheckCircle.vue'
import IconExclamationCircle from '@/components/icons/IconExclamationCircle.vue'
import type { AlertID, AlertType } from '@/libs/alertPortal'
import { ALERT_TYPES } from '@/libs/alertPortal'

export { ALERT_TYPES }

const AUTO_HIDE_DURATION: Millisecond = 10000

type Data = {
  timeoutId: Maybe<number>
}

export default defineComponent({
  name: 'DolphinAlertItem',
  components: {
    ButtonClose,
    IconCheckCircle,
    IconExclamationCircle,
  },
  data(): Data {
    return {
      timeoutId: undefined,
    }
  },
  props: {
    message: {
      type: String,
      default: '',
    },
    type: {
      type: String as PropType<AlertType>,
      default: ALERT_TYPES.NOTICE,
    },
    id: {
      type: Number as PropType<Maybe<AlertID>>,
      default: undefined,
    },
    autoHide: {
      type: Boolean,
      default: false,
    },
  },
  mounted() {
    if (this.autoHide) {
      this.setHideTimeout()
    }
  },
  beforeUnmount() {
    this.clearHideTimeout()
  },
  computed: {
    rootClassnames(): string[] {
      return [this.$style.alert, this.$style[`alert--${this.type}`]]
    },
    isPositive(): boolean {
      return this.type === ALERT_TYPES.SUCCESS
    },
  },
  methods: {
    onClose(): void {
      this.$emit('close', this.id)
    },
    setHideTimeout(): void {
      this.timeoutId = window.setTimeout(this.onClose, AUTO_HIDE_DURATION)
    },
    clearHideTimeout(): void {
      if (this.timeoutId === undefined) {
        return
      }
      window.clearTimeout(this.timeoutId)
    },
    onMouseover(): void {
      if (this.autoHide) {
        this.clearHideTimeout()
      }
    },
    onMouseout(): void {
      if (this.autoHide) {
        this.setHideTimeout()
      }
    },
  },
})
</script>

<style lang="scss" module>
.alert {
  $root: &;

  position: relative;
  display: flex;
  overflow: hidden;
  box-sizing: border-box;
  align-items: center;
  padding: 18px 24px;
  background-color: $color-surface-secondary-opaque;
  border-radius: $border-radius-v2-base;

  &--success {
    background-color: $color-success-surface;
  }

  &--error {
    background-color: $color-caution-surface;
  }

  &--warning {
    background-color: $color-warning-surface;
  }

  &__icon {
    color: $color-text-and-object-low-emphasis;
    font-size: 26px;
    fill: currentcolor;

    &--success {
      color: $color-success-accent;
    }

    &--error {
      color: $color-caution-accent;
    }

    &--warning {
      color: $color-warning-accent;
    }
  }

  &__message {
    flex: 1 1 auto;
    padding-bottom: 1px;
    color: $color-text-and-object-low-emphasis;
    font-size: 18px;
    font-weight: bold;
    white-space: pre-wrap;

    &:nth-child(n + 2) {
      margin-left: 24px;
    }

    &--success {
      color: $color-success-object;
    }

    &--error {
      color: $color-caution-accent;
    }

    &--warning {
      color: $color-warning-object;
    }
  }

  &__button {
    box-sizing: border-box;
    flex: 0 0 24px;
    padding: 5px;
    color: $color-text-and-object-low-emphasis;
    font-size: 18px;
    transition: color $transition-interaction;

    &:hover {
      opacity: 0.7;
    }

    &:active {
      opacity: 0.7;
    }

    &--success {
      color: $color-success-object;
    }

    &--error {
      color: $color-caution-accent;
    }

    &--warning {
      color: $color-warning-object;
    }
  }
}
</style>
